import React, { useState } from 'react'
import { Popover } from 'antd'
import AiDraftWorkflowOrchestrator from './WorkflowOrchestrator'
import AiDraftErrorBoundary from './ErrorBoundary'
import type { AiDraftPopoverProps } from './types'
import { WorkflowState } from '../../../models/aiAnalysis/workflow'
import { Requests, ReactQuery } from '../../../api'
import styles from './styles.module.scss'

const POPOVER_WIDTH = '333px'

const suppressionKey = (actionId: string) => `ai-draft-suppressed-${actionId}`

const AiDraftPopover: React.FC<AiDraftPopoverProps> = ({
  forwardAction,
  acknowledgeAction,
  autoOpen,
  placement = 'topRight',
  maxWidth = '90vw'
}) => {
  const key = suppressionKey(forwardAction?.id ?? acknowledgeAction?.id ?? 'unknown')
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (!autoOpen) return false
    const suppressed = !!sessionStorage.getItem(key)
    if (suppressed) sessionStorage.removeItem(key)
    return !suppressed
  })
  const [currentWorkflowState, setCurrentWorkflowState] = useState<WorkflowState | null>(null)

  // Build pre-decided placeholders when only one action is provided by the backend.
  // The backend query only returns pending actions, so if forward was already decided
  // externally, we only receive the acknowledge action.
  const resolvedForward = forwardAction ?? {
    id: 'pre-decided-forward',
    action_type: 'forward' as const,
    action_payload: { text: '' },
    order_id: 0,
    created_at: '',
    updated_at: '',
    decided_at: new Date().toISOString(),
    decision: 'approved' as const
  }

  const resolvedAcknowledge = acknowledgeAction ?? {
    id: 'pre-decided-acknowledge',
    action_type: 'acknowledge' as const,
    action_payload: { text: '' },
    order_id: 0,
    created_at: '',
    updated_at: '',
    decided_at: new Date().toISOString(),
    decision: 'approved' as const
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleFatalDismiss = () => {
    sessionStorage.setItem(key, '1')
    handleClose()
  }

  const handleErrorBoundaryClose = () => {
    // Best-effort decline of pending actions before closing.
    const declineAction = (id: string) => {
      const request = Requests.updateAiAction({ actionId: id, decision: 'declined' })
      ReactQuery.patchResource(request, request.data).catch(() => {})
    }

    if (resolvedForward.decision === null) declineAction(resolvedForward.id)
    if (resolvedAcknowledge.decision === null) declineAction(resolvedAcknowledge.id)

    handleClose()
  }

  const handleWorkflowStateChange = (state: WorkflowState) => {
    setCurrentWorkflowState(state)
  }

  const isSecondMessage =
    currentWorkflowState === WorkflowState.ACKNOWLEDGE_DRAFT || (!forwardAction && acknowledgeAction != null)

  return (
    <Popover
      content={
        <AiDraftErrorBoundary onClose={handleErrorBoundaryClose}>
          <AiDraftWorkflowOrchestrator
            forwardAction={resolvedForward}
            acknowledgeAction={resolvedAcknowledge}
            isOpen={isOpen}
            onClose={handleClose}
            onFatalDismiss={handleFatalDismiss}
            onWorkflowStateChange={handleWorkflowStateChange}
          />
        </AiDraftErrorBoundary>
      }
      placement={placement}
      open={isOpen}
      trigger={[]}
      overlayClassName={`ai-draft-popover-overlay ${isSecondMessage ? 'ai-draft-popover-second-message' : ''}`}
      overlayInnerStyle={{
        width: POPOVER_WIDTH,
        maxWidth: maxWidth
      }}
      align={{
        overflow: { adjustX: true, adjustY: false }
      }}
      getPopupContainer={() => document.body}
    >
      <div className={`${styles.anchor} ai-draft-popover-anchor`} data-latest-draft={autoOpen ? 'true' : undefined} />
    </Popover>
  )
}

export default AiDraftPopover
